import React, { useState } from "react";
import ArticleContent from './ArticleContent';
import { useRouter } from 'next/router';
import ProducersPanel from './ProducersPanel';
import Head from 'next/head';
import { formatTitle } from '../../../utils/helper';
import Loading from '../Loading';
import {
  getArticleFromSlug,
  getArticleProducers,
  getArticleRelatedArticles
} from '../../../lib/articleRequests';
import LockedContentWrapper from ".././Wrappers/LockedContentWrapper";
import { useAuth } from "../../../context/authContext";
import { useValidSubscription } from "../../../hooks/useValidSubscription";
import Link from "next/link";
import { useToast } from "../../../context/toastContext";

interface IArticleProps {
  article?: any;
  rawContent?: string;
  slug?: string;
  imageUrl?: string;
  error?: string;
}

const Article = ({article, slug, rawContent: articleRawContent, error, imageUrl}: IArticleProps) => {
  const [loadingArticle, setLoadingArticle] = useState(true);
  const router = useRouter();
  const toast = useToast();
  const [producersPanelOpen, setProducersPanelOpen] = useState(false);
  const [rawContent, setRawContent] = useState(undefined);
  const [rawContentPreview, setRawContentPreview] = useState(undefined);
  const [producers, setProducers] = useState(undefined);
  const [relatedArticles, setRelatedArticles] = useState(undefined);
  const [mounted, setMounted] = React.useState(false);

  const {
    state: { user, loading: loadingAuth },
  } = useAuth();

  const hasValidSubscription = useValidSubscription();

  const toggleProducersPanel = (e) => {
    setProducersPanelOpen(!producersPanelOpen);
  }

  const redirectToSignIn = () => {
    router.push({ pathname: "/users/sign_in", query: { from: router.asPath } });
  }

  React.useEffect(() => {
    if (error) {
      toast("error", error);
      return;
    }
    if (slug && article?.slug !== slug) {
      let content;
      // Try to make markup seo friendly, otherwise return raw content
      try {
        content = getSeoFriendlyContent(articleRawContent, article);
      } catch (e) {
        content = articleRawContent;
      }

      // Replace outdated URLs
      content = content.replace(/vinousmedia\.com\/articles/gis, "vinous.com\/articles");

      setRawContent(content);
      getArticleProducers(article.id).then((responseProducers) => {
        setProducers(responseProducers.data);
      });

      getArticleRelatedArticles(article.id).then((responseArticles) => {
        setRelatedArticles(responseArticles.data);
      });

      // Try to separate a percentage of full article to display when user is not signed in
      // Fallback to article preview
      try {
        const rawContentList = [...content?.matchAll(/<p.*?<\/p>/sg)];
        if (rawContentList) {
          // Grab a larger percentage when article is smaller
          const previewLength = rawContentList.length > 50 ? 0.15 : 0.25;
          // Add title separately if converted to h1 tag for seo
          const htagTitle = content.match(/<h1.*?<\/h1>/s);
          const preview = rawContentList.slice(0, Math.ceil(rawContentList.length * previewLength)).join("");
          setRawContentPreview(htagTitle ? htagTitle + preview : preview);
        } else {
          setRawContentPreview(article.preview);
        }
      } catch (e) {
        setRawContentPreview(article.preview);
      }
    }
  }, [slug, article?.slug]);


  React.useEffect(() => {
    if (mounted && !loadingAuth) {
      setLoadingArticle(false);
    } 
  }, [mounted, loadingAuth]);

  React.useEffect(() => {
    setMounted(true);
  } , []);

  const getSeoFriendlyContent = (content, article) => {
    // Assume first centered <p> is title and wrap in <h1>
    let seoFriendlyContent = content.replace(/(<p)( class="wysiwyg-text-align-center">.*?)(<\/p>)/i, "<h1$2</h1>");
    // Try to find published date and wrap in <time>
    seoFriendlyContent = seoFriendlyContent.replace(/(<p.*?\|)(.*?)(<\/p>)/i, `$1<time datetime="${article.date}" pubdate>$2</time>$3`);
    // Use italicized text under images as alt text
    seoFriendlyContent = seoFriendlyContent.replace(/(<img )(alt="")(.*?<\/p>.*?<p>)(<i>(.*?)<\/i>)/gis, `$1alt="$5"$3$4`);
    return seoFriendlyContent;
  }


  function addArticleJsonLd() {
    const jsonLd = {
      "@context": "https://www.schema.org",
      "@type": "Article",
      "name": article?.title,
      "author": {
        "@type": "Person",
        "name": article?.author,
      },
      "datePublished": article?.date,
      "image": imageUrl,
      "articleBody": article?.preview,
      "headline": article?.title,
      "countryOfOrigin": article?.country,
    }
    return JSON.stringify(jsonLd, null, 2);
  }

  return (
      <>
        <Head>
          <title>{formatTitle(article?.title)}</title>
          {article?.keywords.length > 0 && <meta name="keywords" content={article?.keywords.join(',')}/>}
          <meta name="author" content={article?.author}/>
          <meta name="description" content={article?.preview}/>
          <meta name="viewport" content="width=device-width, initial-scale=1"/>
          <link rel="canonical" href={`https://vinous.com/articles/${article?.slug}`}/>

          <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{__html: addArticleJsonLd()}}
              key="article-jsonld"
          />
        </Head> 
      <article className="min-h-screen">   
        {  loadingArticle ?
              <Loading isLoading={loadingArticle}/> :
              <div className={`lg:grid lg:grid-cols-3`}>
                <div 
                  className="lg:col-span-2 flex flex-col xl:pl-20 xl:pr-20 lg:border-r-2 lg:border-gray-200 cursor-auto" 
                  onClick={() => setProducersPanelOpen(false)} 
                  onKeyDown={(e) => e.key === "Enter" && setProducersPanelOpen(false)}
                  tabIndex={0}
                  role="button"
                >
                  {article?.no_paywall || hasValidSubscription || article?.slug === 'josh-raynolds-in-memoriam-1962-2023-mar-2023' ? <ArticleContent rawContent={article && rawContent}></ArticleContent> :
                      <React.Fragment>
                        <ArticleContent rawContent={rawContentPreview}></ArticleContent>
                        <LockedContentWrapper
                            message={
                              <div className="space-y-4 text-center">
                                <div className="flex items-center justify-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-black"
                                       fill="none"
                                       viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                  </svg>
                                  <p className="pt-1 pl-3 text-2xl">
                                    Subscriber Access Only
                                  </p>
                                </div>
                                {!user ? (
                                  <p className="text-sm">
                                    <button
                                      onClick={() => redirectToSignIn()}
                                      className="text-red"
                                    >
                                      Log In
                                    </button>{" "}
                                    or{" "}
                                    <Link
                                      href="/users/plan"
                                      className="text-red"
                                    >
                                      Sign Up
                                    </Link>
                                  </p>
                                ) : (
                                  <p className="text-sm">
                                    <Link href="/users/edit" className="text-red">
                                      Renew or Subscribe
                                    </Link>{" "}
                                    to access this content{" "}
                                  </p>
                                )}
                              </div>
                            }
                            placeholderContent={
                              <div>
                                <p className="pb-2">
                                  {article?.preview}
                                </p>
                              </div>
                            }
                        >
                          <ArticleContent rawContent={article && rawContent}></ArticleContent>
                        </LockedContentWrapper>
                      </React.Fragment>
                  }
                </div>
              { article && <div className="lg:col-span-1 lg:inline-flex lg:justify-center">
                  <ProducersPanel
                      className={`lg:w-full lg:pl-10`}
                      article={article}
                      producers={producers}
                      relatedArticles={relatedArticles}
                      producersPanelOpen={producersPanelOpen}
                      toggleProducersPanel={toggleProducersPanel}
                  />
                </div>
              }
              </div> 
        }
      </article>
    </>
  )
}

export default Article;
